import { useState, useEffect } from 'react'
import { fetchAdoptions } from '../services/api'

const fallbackSponsorPets = [
  {
    id: 1,
    name: "Bella",
    image: "/adopters/1.png",
    story: "Bella was rescued from the streets after a severe leg injury. With your support, she is now healing and living a happy life.",
    needs: ["Needs regular medical care", "Nutritious food & supplements", "A safe and loving shelter"],
    cost: "₹1,500"
  },
  {
    id: 2,
    name: "Scooby",
    image: "/adopters/2.png",
    story: "Scooby was found shivering under an overpass during heavy monsoon rains. Quiet, vaccinated, and seeking a sponsor.",
    needs: ["Daily food and kibble", "Deworming & vaccinations", "Leash training & socialization"],
    cost: "₹1,200"
  },
  {
    id: 3,
    name: "Bruno",
    image: "/adopters/3.png",
    story: "Bruno recovered from a fracture after a vehicle collision. Slowly learning to trust humans again and needs stable care.",
    needs: ["Physiotherapy sessions", "Calcium & bone supplements", "A cozy bed & daily care"],
    cost: "₹1,800"
  },
  {
    id: 4,
    name: "Mitzi",
    image: "/adopters/4.png",
    story: "Mitzi was abandoned outside a clinic. Calm and affectionate, she gets along well with other pets and needs senior care.",
    needs: ["Senior health checkups", "Special digestive food", "Comfortable indoor shelter"],
    cost: "₹1,600"
  }
]

const SponsorBanner = ({ onOpenDonate }) => {
  const [pets, setPets] = useState(fallbackSponsorPets)
  const [activeIndex, setActiveIndex] = useState(0)
  const [isFading, setIsFading] = useState(false)

  useEffect(() => {
    fetchAdoptions().then((data) => {
      if (data && data.length > 0) {
        const formatted = data.map((pet) => ({
          id: pet.id,
          name: pet.name,
          image: pet.image_url || '/adopters/1.png',
          story: pet.description || `${pet.name} is a ${pet.age} ${pet.breed} looking for love, care, and sponsorship.`,
          needs: [
            `Age & Breed: ${pet.age || 'Young'} ${pet.breed || 'Rescue'}`,
            "Nutritious food & medical care",
            "Safe sanctuary boarding"
          ],
          cost: pet.cost || "₹1,500"
        }))
        setPets(formatted)
      }
    })
  }, [])

  useEffect(() => {
    if (!pets.length) return
    const interval = setInterval(() => {
      setIsFading(true)
      setTimeout(() => {
        setActiveIndex((prev) => (prev + 1) % pets.length)
        setIsFading(false)
      }, 400)
    }, 6000)
    
    return () => clearInterval(interval)
  }, [pets])

  const handleDotClick = (idx) => {
    if (idx === activeIndex || isFading) return
    setIsFading(true)
    setTimeout(() => {
      setActiveIndex(idx)
      setIsFading(false)
    }, 400)
  }

  const currentPet = pets[activeIndex] || pets[0]

  return (
    <div className="hidden lg:flex max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 mt-4 sm:mt-6 lg:mt-8 relative py-4 sm:py-6 lg:py-12 flex-col items-center select-none">

      {/* Desktop Main CTA Card Layout (Screens >= lg) */}
      <div className="hidden lg:flex w-full relative bg-[#fffdfb] border border-[#ffded4] rounded-[32px] shadow-lg shadow-brand-orange-500/5 flex-col lg:flex-row items-stretch overflow-hidden pb-6 lg:pb-0">
        
        {/* Animated Inner Content Wrapper */}
        <div className={`w-full flex flex-col lg:flex-row items-stretch transition-all duration-500 ${isFading ? 'opacity-0 scale-99 blur-[1px]' : 'opacity-100 scale-100 blur-0'}`}>
          
          {/* Left Side: Dog Image */}
          <div className="w-full lg:w-[26%] shrink-0 h-64 lg:h-auto relative overflow-hidden">
            <img
              src={currentPet.image}
              alt={`${currentPet.name} the rescued dog`}
              className="w-full h-full lg:absolute lg:inset-0 object-cover select-none pointer-events-none transition-all duration-700"
            />
          </div>

          {/* Right Side: Information Grid */}
          <div className="flex-grow grid grid-cols-1 md:grid-cols-12 gap-8 p-6 sm:p-10 lg:p-12 items-center">
            
            {/* Column 1: Meet Pet & Story */}
            <div className="md:col-span-4 flex flex-col justify-center text-left space-y-3">
              <h2 
                className="text-4xl font-normal text-brand-orange-700 leading-none flex items-center gap-1.5 select-none"
                style={{ fontFamily: 'var(--app-font-heading)' }}
              >
                Meet {currentPet.name} <span className="text-3xl font-sans font-light select-none">♡</span>
              </h2>
              <p className="text-xs sm:text-sm lg:text-[14px] text-slate-500 font-semibold leading-relaxed max-w-[320px]">
                {currentPet.story}
              </p>
            </div>

            {/* Column 2: Needs Checklist & Monthly Cost Pill */}
            <div className="md:col-span-4 flex flex-col justify-center text-left space-y-4">
              <ul className="space-y-2.5 text-xs sm:text-sm lg:text-[14px] text-slate-600 font-bold select-none">
                {currentPet.needs.map((need, idx) => (
                  <li key={idx} className="flex items-center gap-2.5">
                    <svg className="w-5 h-5 text-brand-orange-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <circle cx="12" cy="12" r="9" className="stroke-brand-orange-500" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75" />
                    </svg>
                    <span>{need}</span>
                  </li>
                ))}
              </ul>

              {/* Monthly Cost Pill */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-orange-50/70 border border-brand-orange-100/50 rounded-xl text-xs sm:text-sm font-bold text-slate-700 select-none self-start">
                <svg className="w-4.5 h-4.5 text-brand-orange-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5m-9-6h.008v.008H12v-.008zM12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z" />
                </svg>
                <span>
                  Monthly care cost: <span className="text-brand-orange-700 text-sm lg:text-base font-black">{currentPet.cost}</span>
                </span>
              </div>
            </div>

            {/* Column 3: Dashed Action Box */}
            <div className="md:col-span-4 flex flex-col justify-center w-full">
              <div className="border-2 border-dashed border-[#ffded4] bg-brand-orange-50/15 rounded-[24px] p-6 flex flex-col items-center justify-center text-center space-y-4">
                
                <div className="space-y-1.5 w-full flex flex-col items-center">
                  <div className="flex items-center justify-center gap-1.5 text-slate-800">
                    <svg className="w-5 h-5 text-brand-orange-500 fill-none stroke-current" viewBox="0 0 24 24" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                    </svg>
                    <h4 className="font-display font-semibold text-xs sm:text-sm lg:text-base tracking-tight text-slate-800 leading-none">Be {currentPet.name}'s Hope</h4>
                  </div>
                  <p className="text-[10px] sm:text-xs lg:text-[13px] text-slate-400 font-bold leading-tight">
                    Sponsor {currentPet.name === 'Bella' || currentPet.name === 'Mitzi' ? 'her' : 'him'} and change {currentPet.name === 'Bella' || currentPet.name === 'Mitzi' ? 'her' : 'his'} world.
                  </p>
                </div>
                
                <button
                  onClick={onOpenDonate}
                  className="w-full py-3 bg-brand-orange-700 hover:bg-brand-orange-855 text-white font-semibold rounded-xl text-xs sm:text-sm cursor-pointer flex items-center justify-center gap-1.5 transition-all duration-300 hover:scale-102 active:scale-98"
                >
                  Sponsor {currentPet.name}
                  <svg className="w-3.5 h-3.5 text-white fill-none stroke-current" viewBox="0 0 24 24" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                  </svg>
                </button>

              </div>
            </div>

          </div>

        </div>

        {/* Pagination Dots at Bottom Center of Card */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20 select-none">
          {pets.map((_, idx) => (
            <button
              key={idx}
              onClick={() => handleDotClick(idx)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                idx === activeIndex ? 'bg-brand-orange-500 w-5' : 'bg-brand-orange-200 hover:bg-brand-orange-300'
              }`}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>

      </div>

      {/* Sponsor a Life Today Desktop Action Area */}
      <div className="hidden lg:flex mt-6 sm:mt-10 flex-col items-center justify-center w-full gap-2">
        <button
          onClick={onOpenDonate}
          className="relative z-10 px-8 sm:px-10 py-3.5 sm:py-4.5 bg-brand-orange-700 hover:bg-brand-orange-855 text-white font-semibold rounded-full shadow-lg shadow-brand-orange-500/20 hover:shadow-brand-orange-500/30 text-sm sm:text-base cursor-pointer flex items-center justify-center gap-2.5 transition-all duration-300 hover:scale-103 active:scale-95"
        >
          <svg className="w-5 h-5 text-white fill-current" viewBox="0 0 24 24">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
          </svg>
          Sponsor a Life Today
        </button>

        <span className="text-sm text-slate-500 font-semibold tracking-wide select-none">
          Every contribution makes a difference
        </span>
      </div>

    </div>
  )
}

export default SponsorBanner
